/**
 * Message de confirmation affiché avant la publication d'un concours.
 *
 * Centralisé ici (DRY) car partagé par CompetitionCreate, CompetitionEdit et
 * CompetitionList. Le verrou prix C2 (le tarif est figé dès la publication)
 * impose d'avertir le gestionnaire de façon identique partout.
 */
export const PUBLISH_CONFIRM_MESSAGE =
  'Publier ce concours ?\n\n' +
  '• Les inscriptions seront ouvertes immédiatement.\n' +
  '• Le tarif ne pourra plus être modifié une fois le concours publié (le prix affiché = prix payé par les capitaines).\n\n' +
  'Pour changer le prix ensuite, il faudra cloner le concours dans un nouveau brouillon.';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;

/**
 * Avertissement (ou null) sur les rappels automatiques Temporal qui NE seront
 * PAS envoyés si la date du concours est trop proche.
 *
 * Le workflow `competitionLifecycleWorkflow` skippe silencieusement un rappel
 * dont l'échéance est déjà passée au moment de la publication :
 *  - date < 7 jours  → rappel email J-7 non envoyé
 *  - date < 1 jour   → rappels J-7 ET J-1 non envoyés
 *
 * Exporté pour l'affichage inline non bloquant sous le champ date (création /
 * édition) ET pour enrichir le message de confirmation de publication.
 *
 * @returns le texte d'avertissement, ou `null` si la date est >= 7 j, absente,
 *          invalide ou déjà passée (cas passé géré par la validation existante).
 */
export function reminderSkipWarning(competitionDate?: string | Date | null): string | null {
  if (!competitionDate) return null;
  const date = competitionDate instanceof Date ? competitionDate : new Date(competitionDate);
  if (Number.isNaN(date.getTime())) return null;

  const msUntil = date.getTime() - Date.now();
  if (msUntil <= 0) return null; // date passée → géré par la validation date

  if (msUntil < ONE_DAY_MS) {
    return (
      'La date du concours est dans moins de 24 h : aucun email de relance automatique ' +
      '(ni J-7, ni J-1) ne sera envoyé aux capitaines inscrits.'
    );
  }
  if (msUntil < SEVEN_DAYS_MS) {
    return (
      "La date du concours est dans moins de 7 jours : l'email de relance automatique J-7 " +
      'ne sera pas envoyé aux capitaines inscrits (le rappel J-1 reste programmé).'
    );
  }
  return null;
}

/**
 * Construit le message de confirmation de publication en y ajoutant, le cas
 * échéant, l'avertissement sur les rappels automatiques non envoyés
 * (cf. {@link reminderSkipWarning}). Utilisé par les 3 points de publication.
 */
export function buildPublishConfirmMessage(competitionDate?: string | Date | null): string {
  const warning = reminderSkipWarning(competitionDate);
  return warning ? `${PUBLISH_CONFIRM_MESSAGE}\n\n⚠️ ${warning}` : PUBLISH_CONFIRM_MESSAGE;
}

/**
 * Erreurs de cohérence temporelle bloquant la PUBLICATION : un concours ne peut
 * être publié que si sa date et sa clôture des inscriptions sont dans le futur.
 * Publier dans le passé ouvrirait des inscriptions immédiatement fermées et
 * priverait le workflow Temporal de toute échéance utile.
 *
 * Appliqué uniquement à la publication (pas à l'enregistrement d'un brouillon ni
 * à l'édition générique) pour ne pas bloquer la correction d'un concours passé.
 * Miroir de la garde authoritative côté API (`competitions.service.publish`).
 *
 * @returns un objet d'erreurs par champ (vide si tout est cohérent).
 */
export function publishDateErrors(
  competitionDate?: string | Date | null,
  registrationEnd?: string | Date | null,
): { date?: string; registrationEnd?: string } {
  const errors: { date?: string; registrationEnd?: string } = {};
  const now = Date.now();

  const toMs = (v?: string | Date | null): number | null => {
    if (!v) return null;
    const t = (v instanceof Date ? v : new Date(v)).getTime();
    return Number.isNaN(t) ? null : t;
  };

  const dateMs = toMs(competitionDate);
  if (dateMs !== null && dateMs <= now) {
    errors.date = 'La date du concours doit être dans le futur pour publier';
  }

  const regEndMs = toMs(registrationEnd);
  if (regEndMs !== null && regEndMs <= now) {
    errors.registrationEnd = 'La clôture des inscriptions doit être dans le futur pour publier';
  }

  return errors;
}
